import { normalizeOrderLinesFromApi, orderLineUsesFixedPricing } from '@/models/OrderItemOrderLine';

/**
 * One place that derives order money from the lines themselves (unit price × qty), instead of
 * trusting the stored `final_net_price_with_quantity` / `price_info` totals, which go stale when a
 * qty, a price or a whole product changes without the price breakdown being re-saved.
 *
 * Same rules as the price breakdown modal:
 *  - size row  = size qty × unit price
 *  - add-on / logo technology row = garment qty of that product × unit price
 *  - manual order lines: fixed = qty × price; percentage = pct of (products + fixed lines)
 *  - discount (percent or fixed amount) comes off products only; order lines and shipping are added in full
 */

type AnyRecord = Record<string, any>;

/** Round to 2 d.p. the same way `formatMoney` does (`toFixed(2)`), returned as a number. */
export function roundMoney(value: unknown): number {
  const n = toNumber(value);
  return parseFloat(n.toFixed(2));
}

function toNumber(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function toQty(value: unknown): number {
  return Math.max(0, parseInt(String(value ?? 0), 10) || 0);
}

/** Line amount from unit price × qty (unit price rounded to 2 d.p. first, as the modal input does). */
export function lineAmount(unitPrice: unknown, quantity: unknown): number {
  return roundMoney(toQty(quantity) * roundMoney(unitPrice));
}

/** Garment qty of a factory product: sum of size rows, else `prices.total_quantity`. */
export function factoryProductGarmentQty(factoryProduct: AnyRecord | null | undefined): number {
  const sizes = factoryProduct?.prices?.sizes;
  if (Array.isArray(sizes) && sizes.length > 0) {
    const sum = sizes.reduce((acc: number, s: AnyRecord) => acc + (Number(s?.quantity) || 0), 0);
    if (sum > 0) return sum;
  }
  return toQty(factoryProduct?.prices?.total_quantity);
}

function addonRows(prices: AnyRecord): AnyRecord[] {
  const grouped = prices.grouped_addons;
  return [
    ...(Array.isArray(prices.addons) ? prices.addons : []),
    ...(Array.isArray(prices.ungrouped_addons) ? prices.ungrouped_addons : []),
    ...(grouped && typeof grouped === 'object' ? Object.values(grouped as AnyRecord) : []),
    ...(Array.isArray(prices.logo_technologies) ? prices.logo_technologies : []),
  ].filter((row) => row !== null && row !== undefined);
}

/** Factory product total (sizes + add-ons + logo technologies) from unit prices. */
export function computeFactoryProductTotal(factoryProduct: AnyRecord | null | undefined): number {
  const prices = factoryProduct?.prices;
  if (!prices) return 0;
  let cents = 0;
  (Array.isArray(prices.sizes) ? prices.sizes : []).forEach((size: AnyRecord) => {
    cents += Math.round(lineAmount(size?.final_net_price, size?.quantity) * 100);
  });
  // Add-ons follow the roster only when there are size rows (same guard as the modal).
  const hasSizes = Array.isArray(prices.sizes) && prices.sizes.length > 0;
  const garmentQty = factoryProductGarmentQty(factoryProduct);
  addonRows(prices).forEach((row) => {
    const amount = hasSizes
      ? lineAmount(row?.final_net_price, garmentQty)
      : roundMoney(row?.final_net_price_with_quantity);
    cents += Math.round(amount * 100);
  });
  return cents / 100;
}

/** Sum of stored `final_net_price_with_quantity` (what was saved), for mismatch checks. */
export function storedFactoryProductTotal(factoryProduct: AnyRecord | null | undefined): number {
  const prices = factoryProduct?.prices;
  if (!prices) return 0;
  let cents = 0;
  [...(Array.isArray(prices.sizes) ? prices.sizes : []), ...addonRows(prices)].forEach((row) => {
    cents += Math.round(roundMoney(row?.final_net_price_with_quantity) * 100);
  });
  return cents / 100;
}

export interface OrderItemTotals {
  /** Factory products + add-ons, from unit price × qty. */
  productsSubtotal: number;
  /** Manual order lines (fixed and percentage). */
  orderLinesAmount: number;
  /** Products + order lines, before discount and shipping (`price_info.total_price`). */
  listTotal: number;
  discountAmount: number;
  shipping: number;
  /** What the customer pays for this item (`price_info.order_item_final_price`). */
  finalTotal: number;
  /** Garment qty + order line qty (`price_info.total_quantity`). */
  quantity: number;
}

export interface DiscountShipping {
  is_fixed?: unknown;
  fixed_value?: unknown;
  discount?: unknown;
  shipping?: unknown;
}

function orderLinesTotals(orderItem: AnyRecord, productsSubtotal: number): { amount: number; quantity: number } {
  const lines = normalizeOrderLinesFromApi(orderItem?.order_line);
  let fixedCents = 0;
  for (const line of lines) {
    if (!orderLineUsesFixedPricing(line)) continue;
    fixedCents += Math.round(roundMoney((Number(line.quantity) || 0) * toNumber(line.price)) * 100);
  }
  const percentBase = productsSubtotal + fixedCents / 100;
  let cents = 0;
  let quantity = 0;
  for (const line of lines) {
    quantity += Number(line.quantity) || 0;
    const amount = orderLineUsesFixedPricing(line)
      ? roundMoney((Number(line.quantity) || 0) * toNumber(line.price))
      : roundMoney((percentBase * toNumber(line.percentage)) / 100);
    cents += Math.round(amount * 100);
  }
  return { amount: cents / 100, quantity };
}

/**
 * Totals for one order item, derived from its lines. Discount/shipping come from
 * `discountShipping` when given (the modal's live form), else from the item's own `price_info`.
 */
export function computeOrderItemTotals(
  orderItem: AnyRecord | null | undefined,
  discountShipping?: DiscountShipping,
): OrderItemTotals {
  const products = Array.isArray(orderItem?.factory_products) ? orderItem!.factory_products : [];
  let productCents = 0;
  let quantity = 0;
  products.forEach((fp: AnyRecord) => {
    productCents += Math.round(computeFactoryProductTotal(fp) * 100);
    quantity += factoryProductGarmentQty(fp);
  });
  const productsSubtotal = productCents / 100;
  const lines = orderItem ? orderLinesTotals(orderItem, productsSubtotal) : { amount: 0, quantity: 0 };
  quantity += lines.quantity;

  const ds: DiscountShipping = discountShipping ?? orderItem?.price_info ?? {};
  const isFixed = ds.is_fixed === true || ds.is_fixed === 1 || ds.is_fixed === '1';
  const afterDiscount = isFixed
    ? Math.max(0, productsSubtotal - toNumber(ds.fixed_value))
    : Math.max(0, productsSubtotal - (productsSubtotal * toNumber(ds.discount)) / 100);
  const shipping = roundMoney(ds.shipping);
  const listTotal = Math.max(0, roundMoney(productsSubtotal + lines.amount));
  const finalTotal = Math.max(0, roundMoney(afterDiscount + lines.amount + shipping));

  return {
    productsSubtotal,
    orderLinesAmount: lines.amount,
    listTotal,
    discountAmount: roundMoney(productsSubtotal - afterDiscount),
    shipping,
    finalTotal,
    quantity,
  };
}

/** Whether an item carries enough line data to derive its total (otherwise fall back to stored price_info). */
export function hasPricedLines(orderItem: AnyRecord | null | undefined): boolean {
  const products = orderItem?.factory_products;
  if (Array.isArray(products) && products.some((fp: AnyRecord) => fp?.prices)) return true;
  return normalizeOrderLinesFromApi(orderItem?.order_line).length > 0;
}

/** Order totals = sum of its items. */
export function computeOrderTotals(orderItems: AnyRecord[] | null | undefined): {
  listTotal: number;
  finalTotal: number;
  quantity: number;
} {
  let listCents = 0;
  let finalCents = 0;
  let quantity = 0;
  (orderItems ?? []).forEach((item) => {
    const t = computeOrderItemTotals(item);
    listCents += Math.round(t.listTotal * 100);
    finalCents += Math.round(t.finalTotal * 100);
    quantity += t.quantity;
  });
  return { listTotal: listCents / 100, finalTotal: finalCents / 100, quantity };
}

export interface OrderItemTotalCheck {
  /** Final total derived from unit price × qty. */
  computed: number;
  /** Stored `price_info.order_item_final_price` (or next non-zero fallback). */
  stored: number;
  /** Sum of stored line totals (`final_net_price_with_quantity`) across products. */
  storedLines: number;
  /** Stored line totals disagree with price × qty. */
  linesStale: boolean;
  /** Stored item total disagrees with the derived total. */
  totalStale: boolean;
}

/** Compare the stored totals of an item with what its lines add up to (1 cent tolerance). */
export function checkOrderItemTotals(
  orderItem: AnyRecord | null | undefined,
  storedTotal: number,
): OrderItemTotalCheck {
  const products = Array.isArray(orderItem?.factory_products) ? orderItem!.factory_products : [];
  let storedLineCents = 0;
  let computedLineCents = 0;
  products.forEach((fp: AnyRecord) => {
    storedLineCents += Math.round(storedFactoryProductTotal(fp) * 100);
    computedLineCents += Math.round(computeFactoryProductTotal(fp) * 100);
  });
  const computed = computeOrderItemTotals(orderItem).finalTotal;
  return {
    computed,
    stored: storedTotal,
    storedLines: storedLineCents / 100,
    linesStale: Math.abs(storedLineCents - computedLineCents) > 1,
    totalStale: Math.abs(Math.round(storedTotal * 100) - Math.round(computed * 100)) > 1,
  };
}
